'use client';

import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Typography,
  InputAdornment,
} from '@mui/material';
import { Delete as DeleteIcon, ArrowUpward as ArrowUpIcon } from '@mui/icons-material';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { FileMetaDataConfigDto, FileMetaDataFieldConfigDto } from '@/api/ApiClient';

const OPTION_KEYS: OptionKey[] = [
  'characterizationInstruments',
  'characterizationDropdownChoices',
  'synthesisInstruments',
  'synthesisTechniques',
  'columnType',
  'metadataSource',
];

/**
 * File Metadata Tab
 *
 * Allows configuration of file metadata extraction for characterization and synthesis instruments
 */
export const FileMetadataTab: FC = () => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);

  // Selection state
  const [facility, setFacility] = useState<string>('');
  const [charInstrument, setCharInstrument] = useState<string>('');
  const [techniqueId, setTechniqueId] = useState<string>('');
  const [synInstrument, setSynInstrument] = useState<string>('');

  // Config state
  const [config, setConfig] = useState<FileMetaDataConfigDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUserSpecific, setIsUserSpecific] = useState(false);
  const [testFileName, setTestFileName] = useState('');

  // Get available facilities from characterization instruments
  const availableFacilities = useMemo(() => {
    if (!options.characterizationInstruments) return [];
    const facilities = new Set(options.characterizationInstruments.map((inst) => inst.facility || '').filter((f) => f));
    return Array.from(facilities).sort();
  }, [options.characterizationInstruments]);

  // Get available char instruments filtered by facility
  const availableCharInstruments = useMemo(() => {
    if (!options.characterizationInstruments || !facility) return [];
    return options.characterizationInstruments.filter((inst) => inst.facility === facility);
  }, [options.characterizationInstruments, facility]);

  // Get available syn instruments filtered by technique
  // Synthesis instruments have a many-to-many relationship with techniques
  const availableSynInstruments = useMemo(() => {
    if (!options.synthesisInstruments || !techniqueId) return [];
    return options.synthesisInstruments.filter((inst) => inst.synthesisTechniques?.includes(techniqueId));
  }, [options.synthesisInstruments, techniqueId]);

  const loadConfig = useCallback(async () => {
    if (!charInstrument && !synInstrument) return;
    try {
      const loadedConfig = await backend.metaDataConfig.get(charInstrument || null, synInstrument || null);
      if (!loadedConfig) {
        throw new Error('No data received');
      }
      setConfig(loadedConfig);
      setIsUserSpecific(!loadedConfig.isDefault);
    } catch (err: any) {
      log.error('[FileMetadataTab] Error loading config:', err);
      setError(getProblemDetail(err) ?? 'Failed to load file metadata config');
      showSnackbar('Failed to load file metadata config', 'error');
    } finally {
      setLoading(false);
    }
  }, [charInstrument, synInstrument, backend, showSnackbar]);

  /**
   * Loads config for currently selected instruments.
   *
   * The effect intentionally performs only async work (await before any state update) to avoid
   * synchronous setState calls in effect body.
   */
  useEffect(() => {
    if (!charInstrument && !synInstrument) return;
    let cancelled = false;

    const loadSelectedConfig = async () => {
      try {
        const loadedConfig = await backend.metaDataConfig.get(charInstrument || null, synInstrument || null);
        if (cancelled) return;
        if (!loadedConfig) {
          throw new Error('No data received');
        }
        setConfig(loadedConfig);
        setIsUserSpecific(!loadedConfig.isDefault);
      } catch (err: any) {
        if (cancelled) return;
        log.error('[FileMetadataTab] Error loading config:', err);
        setError(getProblemDetail(err) ?? 'Failed to load file metadata config');
        showSnackbar('Failed to load file metadata config', 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSelectedConfig();
    return () => {
      cancelled = true;
    };
  }, [backend, charInstrument, synInstrument, showSnackbar]);

  const resetConfigSelectionState = useCallback((nextLoading: boolean) => {
    setConfig(null);
    setError(null);
    setIsUserSpecific(false);
    setLoading(nextLoading);
  }, []);

  const handleCharacterizationInstrumentChange = useCallback(
    (nextValue: string) => {
      setCharInstrument(nextValue);
      resetConfigSelectionState(Boolean(nextValue || synInstrument));
    },
    [resetConfigSelectionState, synInstrument]
  );

  const handleSynthesisInstrumentChange = useCallback(
    (nextValue: string) => {
      setSynInstrument(nextValue);
      resetConfigSelectionState(Boolean(charInstrument || nextValue));
    },
    [charInstrument, resetConfigSelectionState]
  );

  const handleFacilityChange = useCallback(
    (nextValue: string) => {
      setFacility(nextValue);
      if (nextValue) {
        setTechniqueId('');
        setSynInstrument('');
        resetConfigSelectionState(Boolean(charInstrument));
      }
    },
    [charInstrument, resetConfigSelectionState]
  );

  const handleTechniqueChange = useCallback(
    (nextValue: string) => {
      setTechniqueId(nextValue);
      if (nextValue) {
        setFacility('');
        setCharInstrument('');
        resetConfigSelectionState(Boolean(synInstrument));
      }
    },
    [resetConfigSelectionState, synInstrument]
  );

  const handleSave = useCallback(async () => {
    if (!config) return;

    setSaving(true);
    try {
      if (config.id) {
        await backend.metaDataConfig.update(config);
      } else {
        await backend.metaDataConfig.create(config);
      }
      showSnackbar('File metadata config saved successfully', 'success');
      setLoading(true);
      setError(null);
      await loadConfig();
    } catch (err: any) {
      log.error('[FileMetadataTab] Error saving config:', err);
      showSnackbar(getProblemDetail(err) ?? 'Failed to save file metadata config', 'error');
    } finally {
      setSaving(false);
    }
  }, [config, backend, showSnackbar, loadConfig]);

  // Handle test filename
  const handleTest = useCallback(async () => {
    if (!testFileName || !config) return;

    setTesting(true);
    try {
      const response = await backend.metaDataConfig.test(testFileName, config);
      if (!response || !response.data) {
        throw new Error('No test results received');
      }

      const text = await response.data.text();
      let testResults: Record<string, string>;

      try {
        testResults = JSON.parse(text);
      } catch (parseErr) {
        log.error('[FileMetadataTab] Failed to parse test results:', parseErr);
        throw new Error('Invalid test response format');
      }

      // Update config with test results
      const updatedConfig = { ...config };
      if (updatedConfig.fields) {
        updatedConfig.fields = updatedConfig.fields.map((field) => {
          const result = testResults[field.label || ''];
          return { ...field, testResult: result } as FileMetaDataFieldConfigDto & { testResult?: string };
        });
      }
      setConfig(updatedConfig);
    } catch (err: any) {
      log.error('[FileMetadataTab] Error testing filename:', err);
      showSnackbar('Failed to test filename', 'error');
    } finally {
      setTesting(false);
    }
  }, [testFileName, config, backend, showSnackbar]);

  // Handle add field
  const handleAddField = useCallback(() => {
    if (!config) return;
    const newField: FileMetaDataFieldConfigDto = {
      label: '',
      type: '',
      order: config.fields?.length || 0,
    };
    setConfig({
      ...config,
      fields: [...(config.fields || []), newField],
    });
  }, [config]);

  // Handle delete field
  const handleDeleteField = useCallback(
    (index: number) => {
      if (!config || !config.fields) return;
      const newFields = config.fields.filter((_, i) => i !== index);
      setConfig({ ...config, fields: newFields });
    },
    [config]
  );

  // Handle move field up
  const handleMoveUp = useCallback(
    (index: number) => {
      if (!config || !config.fields || index < 1) return;
      const newFields = [...config.fields];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      setConfig({ ...config, fields: newFields });
    },
    [config]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    (index: number, field: Partial<FileMetaDataFieldConfigDto>) => {
      if (!config || !config.fields) return;
      const newFields = [...config.fields];
      newFields[index] = { ...newFields[index], ...field };
      setConfig({ ...config, fields: newFields });
    },
    [config]
  );

  // Handle create user-specific config
  const handleCreateUserSpecific = useCallback(() => {
    if (!config) return;
    const userConfig: FileMetaDataConfigDto = {
      ...config,
      id: undefined,
      isDefault: false,
    };
    setConfig(userConfig);
    setIsUserSpecific(true);
  }, [config]);

  if (optionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {/* Instrument Selection */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Facility</InputLabel>
          <Select
            value={facility}
            onChange={(e) => handleFacilityChange(e.target.value)}
            label="Facility"
            disabled={!!techniqueId}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableFacilities.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} disabled={!facility}>
          <InputLabel>Characterization Instrument</InputLabel>
          <Select
            value={charInstrument}
            onChange={(e) => handleCharacterizationInstrumentChange(e.target.value)}
            label="Characterization Instrument"
            disabled={!facility || !!techniqueId}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableCharInstruments.map((inst) => (
              <MenuItem key={inst.id} value={inst.id}>
                {inst.id} - {inst.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Synthesis Technique</InputLabel>
          <Select
            value={techniqueId}
            onChange={(e) => handleTechniqueChange(e.target.value)}
            label="Synthesis Technique"
            disabled={!!facility}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {options.synthesisTechniques?.map((tech) => (
              <MenuItem key={tech.id} value={tech.id}>
                {tech.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }} disabled={!techniqueId}>
          <InputLabel>Synthesis Instrument</InputLabel>
          <Select
            value={synInstrument}
            onChange={(e) => handleSynthesisInstrumentChange(e.target.value)}
            label="Synthesis Instrument"
            disabled={!techniqueId || !!facility}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableSynInstruments.map((inst) => (
              <MenuItem key={inst.id} value={inst.id}>
                {inst.id} - {inst.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {charInstrument || synInstrument ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : config ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* User-specific checkbox */}
            {config.isDefault ? (
              <FormControlLabel
                control={<Checkbox checked={isUserSpecific} onChange={(e) => setIsUserSpecific(e.target.checked)} />}
                label="User specific configuration"
                onChange={(e) => {
                  if ((e.target as HTMLInputElement).checked) {
                    handleCreateUserSpecific();
                  }
                }}
              />
            ) : null}

            {/* Metadata Source */}
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Metadata Source</InputLabel>
              <Select
                value={config.metadataSource || ''}
                onChange={(e) => setConfig({ ...config, metadataSource: e.target.value })}
                label="Metadata Source"
                disabled={config.isDefault}
              >
                {options.metadataSource?.map((source) => (
                  <MenuItem key={source.key} value={source.key}>
                    {source.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Fields Table */}
            {(config.metadataSource === 'Filename' || config.metadataSource === 'Manual') && config.fields ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}></TableCell>
                      <TableCell>Label</TableCell>
                      {config.metadataSource === 'Filename' ? <TableCell>Key in filename pattern</TableCell> : null}
                      <TableCell>Type</TableCell>
                      <TableCell>Value from Test</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {config.fields.map((field, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {!config.isDefault && index > 0 ? (
                            <IconButton size="small" onClick={() => handleMoveUp(index)}>
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={field.label || ''}
                            onChange={(e) => handleFieldChange(index, { label: e.target.value })}
                            disabled={config.isDefault}
                            fullWidth
                          />
                        </TableCell>
                        {config.metadataSource === 'Filename' ? (
                          <TableCell>
                            <TextField
                              size="small"
                              value={field.templatePattern || ''}
                              onChange={(e) => handleFieldChange(index, { templatePattern: e.target.value })}
                              disabled={config.isDefault}
                              fullWidth
                            />
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={field.type || ''}
                              onChange={(e) => handleFieldChange(index, { type: e.target.value })}
                              disabled={config.isDefault}
                            >
                              {options.columnType?.map((type) => (
                                <MenuItem key={type.key} value={type.key}>
                                  {type.value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {(field as any).testResult ? (
                            <Typography variant="body2">{(field as any).testResult}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {!config.isDefault ? (
                            <IconButton size="small" onClick={() => handleDeleteField(index)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {!config.isDefault ? (
                  <Box sx={{ p: 1 }}>
                    <Button size="small" onClick={handleAddField} startIcon={<span>+</span>}>
                      Add Field
                    </Button>
                  </Box>
                ) : null}
              </TableContainer>
            ) : null}

            {/* Filename Template */}
            {config.metadataSource === 'Filename' ? (
              <TextField
                label="Filename pattern"
                value={config.filenameTemplate || ''}
                onChange={(e) => setConfig({ ...config, filenameTemplate: e.target.value })}
                disabled={config.isDefault}
                fullWidth
                helperText="Pattern for file names. Use {fieldName} to capture fields, * to ignore parts."
              />
            ) : null}

            {/* Test Filename */}
            <TextField
              label="Test with filename"
              value={testFileName}
              onChange={(e) => setTestFileName(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={handleTest} disabled={!testFileName || testing} size="small">
                      {testing ? <CircularProgress size={16} /> : 'Test'}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            {/* Save Button */}
            {!config.isDefault ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : null
      ) : null}
    </Box>
  );
};
